import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { MuiTheme } from 'config/materialUI';
import { formatISO } from 'date-fns';
import { omit, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import {
  AbsenceTypeFormation,
  AdditionalFormationCategory,
  EmployeeFormation,
  EmployeeFunction,
  PeriodHour
} from 'components/types';

import { FunctionColor } from 'components/enums';

import { getLocalizedMonthNames } from 'utils/date';

import {
  CapacityBalanceResponse,
  CapacityResponse
} from 'RestAPIs/roster/formation';
import { SaveAdditionalCapacityPayload } from 'RestAPIs/scenario-additional-capacity';

import { scenarioAtom } from '../atoms';

import { Box, IconButton, TableCell, Tooltip } from '@mui/material';

import { FontAwesomeSvgIcon } from 'components/shared';
import { NumericTableCell } from 'components/shared/Table';

import { AdditionalCapacityForm } from './components/AddEditAdditionalCapacityDialog';

import { VIEW } from './const';

export const renderPeriodColumnHeaders = (
  periods: PeriodHour[],
  isViewPerWeek: boolean
) => {
  if (!isViewPerWeek) {
    return getLocalizedMonthNames('abbreviated').map((val) => (
      <NumericTableCell key={`period_${val}`}>{val}</NumericTableCell>
    ));
  }
  return periods.map(({ number }) => (
    <NumericTableCell key={`period_${number}`}>{number}</NumericTableCell>
  ));
};

// eslint-disable-next-line max-len
export const isFormationEmployeeFormation = (
  formation:
    | EmployeeFormation
    | AdditionalFormationCategory
    | AbsenceTypeFormation
): formation is EmployeeFormation =>
  (formation as EmployeeFormation).employee?.id !== undefined;

// eslint-disable-next-line max-len
export const isFormationAbsenceTypeFormation = (
  formation:
    | EmployeeFormation
    | AdditionalFormationCategory
    | AbsenceTypeFormation
): formation is AbsenceTypeFormation =>
  (formation as AbsenceTypeFormation).absenceType !== undefined;

// eslint-disable-next-line max-len
export const isFormationAdditionalTypeFormation = (
  formation:
    | EmployeeFormation
    | AdditionalFormationCategory
    | AbsenceTypeFormation
): formation is AbsenceTypeFormation =>
  (formation as AdditionalFormationCategory).categoryName !== undefined;

const FixedTableHeaderForEmployeeFormationDetail = () => {
  const { t } = useTranslation('common');

  return (
    <>
      <TableCell align="right">{t('wa')}</TableCell>
      <TableCell>{t('formation.type')}</TableCell>
    </>
  );
};

const FixedTableHeaderForAbsenceTypeFormationDetail = () => {
  const { t } = useTranslation('common');

  return (
    <>
      <TableCell width="46px" />
      <TableCell>{t('formation.absenceType')}</TableCell>
    </>
  );
};

const FixedTableHeaderForAdditionalFormationDetail = () => {
  const { t } = useTranslation('common');

  return <TableCell colSpan={3}>{t('formation.category')}</TableCell>;
};

export const renderFixedTableHeaderForFormationDetail = (
  formation:
    | EmployeeFormation
    | AbsenceTypeFormation
    | AdditionalFormationCategory
) => {
  if (isFormationEmployeeFormation(formation)) {
    return FixedTableHeaderForEmployeeFormationDetail();
  }
  if (isFormationAbsenceTypeFormation(formation)) {
    return FixedTableHeaderForAbsenceTypeFormationDetail();
  }

  return FixedTableHeaderForAdditionalFormationDetail();
};

export const EmployeeNameCell = ({
  formation
}: {
  formation: EmployeeFormation;
}) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);

  const hasMultiplePlanUnits = scenario
    ? scenario?.costCenters?.length > 1
    : false;
  const isDeployedAtDifferentPlanUnit =
    formation.sourceOrganizationalUnit?.name !==
    formation.deployedOrganizationalUnit?.name;

  return (
    <TableCell>
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Box>{formation.employee.name}</Box>
          {hasMultiplePlanUnits && isDeployedAtDifferentPlanUnit && (
            <Box fontSize="80%">{`${formation.sourceOrganizationalUnit?.name} => ${formation.deployedOrganizationalUnit?.name}`}</Box>
          )}
          {(!hasMultiplePlanUnits || !isDeployedAtDifferentPlanUnit) && (
            <Box fontSize="80%">{formation.sourceOrganizationalUnit?.name}</Box>
          )}
        </Box>
        {formation.excludedFromScenario && (
          <Box display="flex" alignItems="center">
            <Tooltip
              title={t('formation.employeeExcludedFromScenario') as string}
              arrow
            >
              <IconButton size="small" style={{ padding: '0 12px' }}>
                <FontAwesomeSvgIcon icon={faInfoCircle} fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

const FixedCellsForEmployeeFormationDetail = (formation: EmployeeFormation) => (
  <>
    <TableCell align="right">{formation.employment.number}</TableCell>
    <TableCell>{formation.employmentType?.name}</TableCell>
  </>
);

const FixedCellsForAbsenceFormationDetail = (
  formation: AbsenceTypeFormation
) => {
  const { t } = useTranslation('common');

  return (
    <>
      <TableCell width="46px" />
      <TableCell>{t(`formation.${formation.absenceType}`)}</TableCell>
    </>
  );
};

const FixedCellsForAdditionalFormationDetail = (
  formation: AdditionalFormationCategory
) => {
  const { t } = useTranslation('common');
  return (
    <TableCell colSpan={3}>
      {t(`additionalCapacityTypes.${formation.categoryName}`)}
    </TableCell>
  );
};

export const renderFixedCellsForFormationDetail = (
  formation:
    | EmployeeFormation
    | AdditionalFormationCategory
    | AbsenceTypeFormation
) => {
  if (isFormationEmployeeFormation(formation)) {
    return FixedCellsForEmployeeFormationDetail(formation);
  }
  if (isFormationAbsenceTypeFormation(formation)) {
    return FixedCellsForAbsenceFormationDetail(formation);
  }
  return FixedCellsForAdditionalFormationDetail(formation);
};

export type TotalsGraphSeries = {
  name: string;
  data: number[];
  color?: string;
};

export const formatCapacityDataForGraph = (
  capacity: CapacityResponse,
  view,
  t
): TotalsGraphSeries[] => {
  const data: TotalsGraphSeries[] = [];

  const absence = {
    name: t('formationTypes.absenceFormation'),
    data: [...capacity.absenceFormation.periodHours]
      .sort((a, b) => a.year - b.year || a.number - b.number)
      .map(({ hours }) => Math.abs(hours)),
    color: MuiTheme.palette.warning.dark
  };

  if (view === VIEW.GROSS) {
    data.push(absence);
    if (capacity.loanFormation) {
      data.push({
        name: t('formationTypes.loanFormation'),
        data:
          [...(capacity.loanFormation?.periodHours ?? [])]
            .sort((a, b) => a.year - b.year || a.number - b.number)
            .map(({ hours }) => Math.abs(hours)) ?? [],
        color: '#ed7422'
      });
    }
  } else if (capacity.additionalFormation) {
    data.push({
      name: t('formationTypes.additionalFormation'),
      data:
        [...(capacity.additionalFormation?.periodHours ?? [])]
          .sort((a, b) => a.year - b.year || a.number - b.number)
          .map(({ hours }) => Math.abs(hours)) ?? [],
      color: MuiTheme.palette.primary.dark
    });
  }
  data.push({
    name: t('formationTypes.netTeamFormation'),
    data:
      [...(capacity.netTeamFormation?.periodHours ?? [])]
        .sort((a, b) => a.year - b.year || a.number - b.number)
        .map(({ hours }) => hours) ?? [],
    color: MuiTheme.palette.primary.main
  });
  return data;
};

export const formatCapacityBalanceDataForGraph = (
  capacityBalance: CapacityBalanceResponse,
  showPerFunction: boolean,
  t
): TotalsGraphSeries[] => {
  const data: TotalsGraphSeries[] = [];
  if (showPerFunction) {
    capacityBalance.functionCapacityBalances?.forEach(
      (functionCapacityBalance) => {
        if (functionCapacityBalance) {
          data.push({
            name: functionCapacityBalance.function?.description ?? 'unknown',
            data:
              [...functionCapacityBalance.periodHourBalances]
                .sort((a, b) => a.year - b.year || a.number - b.number)
                .map(({ hours }) => hours) ?? [],
            color:
              functionCapacityBalance.function?.colorCode ??
              FunctionColor.UNKNOWN
          });
        }
      }
    );
  } else if (capacityBalance) {
    data.push({
      name: `${t('formation.formation')} ${t('comparedTo')} ${t(
        'analyzer.filters.workDemand'
      )}`,
      data:
        [...capacityBalance.periodHourBalances]
          .sort((a, b) => a.year - b.year || a.number - b.number)
          .map(({ hours }) => hours) ?? [],
      color: FunctionColor.FORMATION
    });
  }
  return data;
};

export const getUniqueFunctionsForCapacity = (
  capacity: CapacityResponse
): EmployeeFunction[] => {
  const allFunctions = (
    capacity.grossFormation.employeeFormations?.map(
      (formation) => formation.function
    ) || []
  )
    .concat(
      capacity.additionalFormation?.additionalFormations?.map(
        (formation) => formation.function
      ) || []
    )
    .filter((fn) => fn);

  return uniqBy(allFunctions, 'id');
};

export const formatAdditionalCapacityDataForSave = (
  capacity: AdditionalCapacityForm
) =>
  ({
    ...omit(capacity, [
      'id',
      'function',
      'scenarioId',
      'organizationalUnit',
      'status',
      'approvedOn',
      'rejectedOn',
      'createdOn',
      'reason',
      'sourceOrganizationalUnit',
      'destinationOrganizationalUnit'
    ]),
    startDate: formatISO(capacity.startDate, { representation: 'date' }),
    endDate: formatISO(capacity.endDate, { representation: 'date' }),
    functionId: capacity.function.id,
    organizationalUnitId:
      capacity.organizationalUnitId.id ?? capacity.organizationalUnitId.brId
  } as SaveAdditionalCapacityPayload);
